/***********************/
/* Angular + external */
import { Component, ElementRef, OnDestroy } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Observable, Subject, Subscription } from "rxjs";
/***********************/
/* BFX */
// models
import { BfxDialogData } from "./models/bfx-dialog-data.model";
// components
import { BfxDialogBaseComponent } from "./bfx-dialog-base.component";
// services
import { BfxConfigService } from "../../shared/auth-services/system-config/bfx-config.service";
import { BfxOrderDialogsProcessorService } from "./services/bfx-order-dialogs-processor.service";
//

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Component({
  selector: "bfx-dialog-order-quick-fill-warning",
  templateUrl: "./html/bfx-dialog-order-quick-fill-warning.html",
  standalone: false,
})
export class BfxDialogOrderQuickFillWarningComponent
  extends BfxDialogBaseComponent
  implements OnDestroy
{
  /***************************************/
  /* DATA */
  public config: any = {
    debug: false,
  };

  public data = {
    dialogId: null,
    dialogEventSubject: new Subject<BfxDialogData>(),
    actionEvent: null,
    payload: null,
    dialogData: {
      show: false,
      width: 450,
      date_format: "HH:mm d-MM-YYYY zz",
      class_name: "bfx-dialog-warning",
      title_template: {
        expired: "Expired Order Warning",
        external: "External Order Warning",
        transfer: "Order(s) in Transfer",
        warning: "WARNING !",
        credit_check: "Order(s) Failed Credit",
        fill: "Fill Limit Breached",
      },
      title_text: null,
      message_text_template: {
        expired_single: "Order =Id= has past its expiry time of =Time=.",
        expired_multi: "=Num= of the selected orders have past their expiry times.",
        external_single:
          "Order =Id= is owned by an external trader group. Acting on this order will not communicate the state change to the external system. This could result in mismatched order books.",
        external_multi:
          "=Num= of the selected orders are owned by an external trade group(s). Acting on these orders will not communicate the state change to the external system(s). This could result in mismatched order books.",
        transfer_single: "Order =Id= is In Transfer to =Loc=.",
        transfer_multi: "=Num= of the selected orders are In Transfer.",
        credit_check_single:
          "Order =Id= failed a Credit check and is awaiting approval in Dealer Intervention.",
        credit_check_multi:
          "=Num= of the selected orders failed a Credit Check and are awaiting approval in Dealer Intervention.",

        fill: "You are filling =Num= orders.",
      },
      message_text: null,
      message_icon_show: null,
      message_question_template: {
        continue: "Are you sure you want to continue?",
      },
      message_question: null,
    },
  };

  private coordinatorSubscription: Subscription;
  private configurationSubscription: Subscription;

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */
  constructor(
    private _configService: BfxConfigService,
    private _translationService: TranslateService,
    private _orderDialogProcessorService: BfxOrderDialogsProcessorService,
    private _element: ElementRef,
  ) {
    super();
    this.subscribeToConfiguration();
  }

  ngOnDestroy() {
    this.configurationSubscription.unsubscribe();
  }

  /**********************************************************************/
  /* INITIALIZATION */
  private subscribeToConfiguration(): void {
    this.configurationSubscription = this._configService.uiConfig$.subscribe((config) => {
      this.config.debug = config.general.debug;
    });
  }

  /**********************************************************************/
  /* QUICK FILL WARNING - Open/Close Dialog */
  public openDialog(_dialogData: BfxDialogData): Observable<any> {
    try {
      this.data.dialogId = _dialogData.dialogId;
      this.data.actionEvent = _dialogData.event;
      this.data.payload = _dialogData.payload;
      let warningData = _dialogData.payload;
      let dialog_type = _dialogData.command;
      if (warningData !== null && warningData.show) {
        let title_text = "";
        let message_text = new Array<string>();
        let message_question = "";
        let message_icon_show = false;
        let class_name = this.data.dialogData.class_name;

        Promise.all(
          new Array(
            this._orderDialogProcessorService.prepareQuickFillWarningMessageText(
              "external",
              _dialogData,
              this.data.dialogData,
            ),
            this._orderDialogProcessorService.prepareQuickFillWarningMessageText(
              "expired",
              _dialogData,
              this.data.dialogData,
            ),
            this._orderDialogProcessorService.prepareQuickFillWarningMessageText(
              "transfer",
              _dialogData,
              this.data.dialogData,
            ),
            this._orderDialogProcessorService.prepareQuickFillWarningMessageText(
              "credit_check",
              _dialogData,
              this.data.dialogData,
            ),
            this._orderDialogProcessorService.prepareQuickFillWarningMessageText(
              "fill",
              _dialogData,
              this.data.dialogData,
            ),
          ),
        ).then((_message_texts: Array<string | null>) => {
          title_text = this.data.dialogData.title_template.warning;
          message_question = this.data.dialogData.message_question_template.continue;
          message_icon_show = true;
          class_name += " warning";
          for (let _message_text of _message_texts) {
            if (_message_text !== null && _message_text.length > 1) {
              message_text.push(_message_text);
            }
          }
          this.assignValuesAndShowWarningDialog(
            title_text,
            message_text,
            message_question,
            message_icon_show,
            class_name,
          );
        });
      } else {
        logger.out.error(
          this.constructor.name + "ERROR: openDialog(): invoked without proper warning data!",
        );
      }
    } catch (ex) {
      logger.out.error(
        this.constructor.name + "ERROR: openDialog(): single warning dialog exception! msg:",
        ex,
      );
    }
    return this.data.dialogEventSubject.asObservable();
  }

  public closeDialog(): void {
    super.closeDialog();

    this.data.dialogData.title_text = null;
    this.data.dialogData.message_text = null;
    this.data.dialogData.message_question = null;
    this.data.dialogData.message_icon_show = null;
    this.data.dialogData.class_name = null;
    this.data.actionEvent = null;
    this.data.payload = null;
    this.data.dialogData.show = false;
    // inform dialog service that dialog is closed and to cleanup
    this.notifyDialogClosed(this.data.dialogEventSubject, this.data.dialogId);
  }

  // public attachTypeClass(): void {
  // 	if (this.data.dialogData.dialog_type === BfxDialogCommand.DIALOG_ORDER_DETAIL_FILL_WARNING_OPEN) {
  // 		this.dialog.nativeElement.className += ' warning';
  // 	}
  // }

  /**********************************************************************/
  /* QUICK FILL WARNING - Actions */
  public actionQuickFillWarning_yes(): void {
    if (this.data.payload.callback) {
      this.data.payload.callback(this.data.actionEvent);
    }

    // if (this.data.payload.callback) {
    //   this.data.payload.callback(this.data.actionEvent);
    // } else {
    //   this.data.dialogEventSubject.next(
    //     new BfxDialogData(this.getConfirmOpenCommand(), null, this.data.actionEvent, null),
    //   );
    // }
    this.closeDialog();
  }

  public actionQuickFillWarning_no(): void {
    if (this.data.payload.callbackNegative) {
      this.data.payload.callbackNegative(this.data.actionEvent);
    }
    this.closeDialog();
  }
  /**********************************************************************/
  /* QUICK FILL WARNING - Helpers */
  private assignValuesAndShowWarningDialog(
    title_text: string,
    message_text: Array<string>,
    message_question: string,
    message_icon_show: boolean,
    class_name: string,
  ): void {
    // assign values
    this.data.dialogData.title_text = title_text;
    this.data.dialogData.message_text = message_text;
    this.data.dialogData.message_question = message_question;
    this.data.dialogData.message_icon_show = message_icon_show;
    this.data.dialogData.class_name = class_name;
    // show the dialog
    this.data.dialogData.show = true;
  }
}
